// RingCentralDataContext.tsx - Manages SMS, Fax, Voicemail data from /getdata API

import React, { createContext, useContext, useEffect, useState, ReactNode, useCallback, useRef } from 'react';
import { isAxiosError } from 'axios';
import axiosApexInstance, { getAccessToken } from '../auth/axiosApexInstance';
import { RC_DATA_GETDATA_URL } from '../Constants';
import { useUser } from "./UserContext";

export interface RingCentralDataItem {
    id: number;
    rowStatus: string;
    workflowStatus: string; // ✅ NEW: to reflect ai.RingCentralData.workflow_status
    rowDate: string;
    rowType: string;
    rowSubtype: string;
    callAction: string;
    callResult: string;
    companyId: string;
    extensionId: string;
    messageId: number;
    callId: string;
    fromNumber: string;
    toNumber: string;
    fromName: string;
    toName: string;
    duration: number;
    filepath: string;
    trantext: string;
    tranId: number;
    recordingId: number;
    conversationId: number;
    jsonText: string;
    createdAt: string;
    hookAt: string;
    readyAt: string;
}


interface RingCentralDataContextType {
    allData: RingCentralDataItem[];
    smsMessages: RingCentralDataItem[];
    voicemails: RingCentralDataItem[];
    callog:  RingCentralDataItem[];
    faxes: RingCentralDataItem[];
    refreshData: () => Promise<void>;
    clearData: () => void;
}

const RingCentralDataContext = createContext<RingCentralDataContextType | undefined>(undefined);
export const RingCentralDataProvider = ({ children }: { children: ReactNode }) => {
    const [allData, setAllData] = useState<RingCentralDataItem[]>([]);
    const { accessToken } = useUser();
    const getDataAbortRef = useRef<AbortController | null>(null);

    const loadData = useCallback(async () => {
        // Wait for both React state token and axiosApexInstance token to be set
        if (!accessToken || !getAccessToken()) {
            return;
        }

        getDataAbortRef.current?.abort();
        const ac = new AbortController();
        getDataAbortRef.current = ac;

        try {
            // Paged list + cancel on re-entry / unmount to avoid "connection reset" on huge payloads
            const response = await axiosApexInstance.get(RC_DATA_GETDATA_URL, { signal: ac.signal });
            if (!ac.signal.aborted) {
                setAllData(response.data);
            }
        } catch (error: unknown) {
            if (isAxiosError(error) && error.code === 'ERR_CANCELED') {
                return;
            }
            if (isAxiosError(error) && (error?.response?.status === 403 || error?.message?.includes('Missing access token'))) {
                return;
            }
            console.error('Failed to load RingCentral data', error);
        }
    }, [accessToken]);

    const clearData = () => {
        setAllData([]);
    };

    // Don't auto-load on mount - only load when explicitly requested via refreshData
    // This prevents 403 errors on page load when token may not be ready

    const smsMessages = allData.filter(d => d.rowType === 'message-store' && d.rowSubtype === 'SMS');
    const voicemails = allData.filter(d => d.rowType === 'message-store' && d.rowSubtype === 'VoiceMail');
    const faxes = allData.filter(d => d.rowType === 'message-store' && d.rowSubtype === 'Fax');
    const callog = allData.filter(d => d.rowType === 'calllog' );

    useEffect(() => {
        return () => {
            getDataAbortRef.current?.abort();
        };
    }, []);

    return (
        <RingCentralDataContext.Provider value={{
            allData,
            smsMessages,
            voicemails,
            faxes,
            callog,
            refreshData: loadData,
            clearData,
        }}>
            {children}
        </RingCentralDataContext.Provider>
    );
};

export const useRingCentralData = (): RingCentralDataContextType => {
    const context = useContext(RingCentralDataContext);
    if (!context) {
        throw new Error('useRingCentralData must be used within a RingCentralDataProvider');
    }
    
    // Lazy-load data when hook is first used (only once per component mount)
    const { refreshData } = context;
    const { accessToken } = useUser();
    const hasInitializedRef = useRef(false);
    
    useEffect(() => {
        const pathname = typeof window !== 'undefined' ? window.location.pathname : '';
        // visit-flow should not auto-fetch RingCentral datasets; it causes noisy calls.
        // We still allow manual refreshData() via menu actions.
        if (pathname.startsWith('/visit-flow')) return;

        // Only load if we have an access token and haven't initialized yet
        if (accessToken && !hasInitializedRef.current && refreshData) {
            hasInitializedRef.current = true;
            refreshData().catch(() => {
                // Reset on error so it can retry if needed
                hasInitializedRef.current = false;
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [accessToken]); // Only depend on accessToken, refreshData is stable
    
    return context;
};
